import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { userFriendlyRoleMap } from "./user-friendly-role-map";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { UntilDestroy } from "@ngneat/until-destroy";

import { environment } from "src/environments/environment";
import { ListViewParticipant } from "src/app/shared/models/participant.model";
import { OrderDetailStore } from "src/app/order-detail/+state/order-detail.store";
import { OrderParticipantPresignStatus } from "src/app/shared/models/order-presign-status";

import { PackageParticipantAction } from "./actions/package-participant-action";

@UntilDestroy()
@Component({
    selector: "app-participant-list",
    templateUrl: "./participant-list.component.html",
    styleUrls: ["./participant-list.component.scss"],
    standalone: false
})
export class ParticipantListComponent implements OnInit, OnDestroy {
  private actionHandler: (ParticipantListUser) => void;

  constructor(
    public store: OrderDetailStore,
  ) {}

  // TODO: Make an api function for getting this map (from the UserRole table).

  @Input() participants: ListViewParticipant[];
  @Input() showSessionLink: boolean;
  @Input() showActions: boolean;
  @Input() participantPresignStatuses: Record<
    number,
    OrderParticipantPresignStatus
  > = {};
  @Input() orderId: number;
  @Input() isOrderLocked: boolean;

  @Output() geturlCalled = new EventEmitter<ListViewParticipant>();
  @Output() editActionCalled = new EventEmitter<ListViewParticipant>();
  @Output() resetLoginActionCalled = new EventEmitter<ListViewParticipant>();
  @Output() removeActionCalled = new EventEmitter<ListViewParticipant>();

  canGetLink$: Observable<boolean>;
  orderSubscription: Subscription;
  productType: string;
  hostMeetingUrl?: string;
  guestMeetingUrl?: string;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  ngOnInit() {
    this.determineCanGetLink();
    this.orderSubscription = this.store.order$.subscribe(
      (order) => {
        this.productType = order.productType;
        this.hostMeetingUrl = order.hostMeetingUrl;
        this.guestMeetingUrl = order.guestMeetingUrl;
      }
    );

    this.sortParticipants('name');
  }

  isVirtualMeeting(): boolean {
    return this.productType?.toUpperCase() === "RIW";
  }

  isHost(participant: ListViewParticipant): boolean {
    return this.isVirtualMeeting() && this.isAttorney(participant);
  }

  isAttorney(participant: ListViewParticipant): boolean {
    return participant?.participantModifiable?.role?.toUpperCase() === "ATTORNEY";
  }

  copyToClipboard(text: string): void {
    if (!text) {
      return;
    }
    navigator.clipboard.writeText(text);
  }

  ngOnDestroy(): void {
    if (this.orderSubscription) {
      this.orderSubscription.unsubscribe();
    }
  }

  determineCanGetLink() {
    this.canGetLink$ = this.store.orderStatus$.pipe(
      map((orderStatus) => {
        return (orderStatus === "READY" || orderStatus === "INPROGRESS");
      })
    );
  }

  getFriendlyRole(participant: ListViewParticipant): string {
    return userFriendlyRoleMap[participant.participantModifiable.role];
  }

  participantHasLink(participant: ListViewParticipant) {
    var isRon =
      this.productType?.toUpperCase() === "REMOTESIGNING" ||
      this.productType?.toUpperCase() === "KNOWNSIGNERRON";
    if (!!participant.participantUrl?.consumerPortalUrl && !isRon) {
      return true;
    }
    return !!participant.participantUrl?.url;
  }

  handleGeturlAction(participant: ListViewParticipant) {
    this.geturlCalled.emit(participant);
  }

  handleEditAction(participant: ListViewParticipant) {
    this.editActionCalled.emit(participant);
  }

  handleResetLoginAction(participant: ListViewParticipant) {
    this.resetLoginActionCalled.emit(participant);
  }

  handleRemoveAction(participant: ListViewParticipant) {
    this.removeActionCalled.emit(participant);
  }

  handleActionCall(action: PackageParticipantAction) {
    this.actionHandler = action.action;
    this.actionHandler(action.participant);
  }

  translateParticipantIdToPreSignStatus(participantId: number) {
    const participantPresignStatus =
      this.participantPresignStatuses[participantId];
    if (!participantPresignStatus || !participantPresignStatus.isPreSign) {
      return "";
    }

    if (participantPresignStatus.isComplete) {
      return "Complete";
    }

    if (participantPresignStatus.isStarted) {
      return "Started";
    }

    return "Not Started";
  }

  isMultiFactorEnabled(): boolean {
    return environment.features.isMultiFactorEnabled === "true";
  }

  sortParticipants(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  
    this.participants.sort((a, b) => {
      const valueA = this.getColumnValue(a, column).toString().toLowerCase();
      const valueB = this.getColumnValue(b, column).toString().toLowerCase();
  
      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      } else if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });
  }
  
  getColumnValue(participant: ListViewParticipant, column: string): any {
    switch (column) {
      case 'name':
        return participant.participantModifiable.lastName;
      case 'role':
        return this.getFriendlyRole(participant);
      case 'email':
        return participant.participantModifiable.email;
      case 'status':
        return this.translateParticipantIdToPreSignStatus(participant.id);
      default:
        return '';
    }
  }

  getSortIcon(column: string): string {
    if (this.sortColumn === column) {
      return this.sortDirection === 'asc' ? 'assets/icons/arrow_up.svg' : 'assets/icons/arrow_down.svg';
    }
    return '';
  }
}
